import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, 
  ArrowLeft, 
  CheckCircle2, 
  User, 
  Building2, 
  MapPin, 
  Phone, 
  Mail,
  Send,
  MessageSquare,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// SVG Roof Types
const RoofSVGs = {
  piano: ({ selected }) => (
    <svg viewBox="0 0 120 80" className={`w-full h-full transition-all ${selected ? 'text-revo-orange' : 'text-gray-600'}`}>
      <rect x="10" y="40" width="100" height="30" fill="currentColor" stroke="currentColor" strokeWidth="2" />
      <rect x="10" y="30" width="100" height="10" fill="currentColor" stroke="currentColor" strokeWidth="2" opacity="0.7" />
      <line x1="10" y1="25" x2="110" y2="25" stroke="currentColor" strokeWidth="3" />
    </svg>
  ),
  pendente_2: ({ selected }) => (
    <svg viewBox="0 0 120 80" className={`w-full h-full transition-all ${selected ? 'text-revo-orange' : 'text-gray-600'}`}>
      <rect x="10" y="50" width="100" height="20" fill="currentColor" stroke="currentColor" strokeWidth="2" />
      <polygon points="10,50 60,15 110,50" fill="currentColor" stroke="currentColor" strokeWidth="2" opacity="0.8" />
      <line x1="60" y1="15" x2="60" y2="50" stroke="currentColor" strokeWidth="1" opacity="0.5" />
    </svg>
  ),
  pendente_1: ({ selected }) => (
    <svg viewBox="0 0 120 80" className={`w-full h-full transition-all ${selected ? 'text-revo-orange' : 'text-gray-600'}`}>
      <rect x="10" y="50" width="100" height="20" fill="currentColor" stroke="currentColor" strokeWidth="2" />
      <polygon points="10,50 10,25 110,50" fill="currentColor" stroke="currentColor" strokeWidth="2" opacity="0.8" />
    </svg>
  ),
  pendente_4: ({ selected }) => (
    <svg viewBox="0 0 120 80" className={`w-full h-full transition-all ${selected ? 'text-revo-orange' : 'text-gray-600'}`}>
      <rect x="20" y="55" width="80" height="15" fill="currentColor" stroke="currentColor" strokeWidth="2" />
      <polygon points="20,55 60,20 100,55" fill="currentColor" stroke="currentColor" strokeWidth="2" opacity="0.8" />
      <polygon points="20,55 10,55 10,40 20,55" fill="currentColor" stroke="currentColor" strokeWidth="2" opacity="0.6" />
      <polygon points="100,55 110,55 110,40 100,55" fill="currentColor" stroke="currentColor" strokeWidth="2" opacity="0.6" />
    </svg>
  )
};

// Building Types
const buildingTypes = {
  privato: [
    { id: 'villa', label: 'Villa Unifamiliare', icon: '🏠' },
    { id: 'condominio', label: 'Condominio', icon: '🏢' },
    { id: 'bifamiliare', label: 'Bifamiliare', icon: '🏘️' },
    { id: 'altro_civile', label: 'Altro Civile', icon: '🏛️' }
  ],
  azienda: [
    { id: 'capannone', label: 'Capannone Industriale', icon: '🏭' },
    { id: 'ufficio', label: 'Ufficio / Commerciale', icon: '🏬' },
    { id: 'magazzino', label: 'Magazzino Logistico', icon: '📦' },
    { id: 'altro_industriale', label: 'Altro Industriale', icon: '⚙️' }
  ]
};

// Roof types configuration
const roofTypes = [
  { id: 'piano', label: 'Tetto Piano', component: RoofSVGs.piano },
  { id: 'pendente_1', label: '1 Falda', component: RoofSVGs.pendente_1 },
  { id: 'pendente_2', label: '2 Falde', component: RoofSVGs.pendente_2 },
  { id: 'pendente_4', label: '4+ Falde', component: RoofSVGs.pendente_4 }
];

// Service types
const serviceTypes = [
  { id: 'progettazione', label: 'Solo Progettazione', description: 'Studio e progetto tecnico' },
  { id: 'installazione', label: 'Installazione Completa', description: 'Progettazione + installazione' },
  { id: 'revisione', label: 'Revisione Periodica', description: 'Controllo sistema esistente' },
  { id: 'completo', label: 'Servizio Completo', description: 'Tutto incluso + manutenzione' }
];

// Italian provinces (complete list with new provinces)
const provinces = [
  'Agrigento', 'Alessandria', 'Ancona', 'Aosta', 'Arezzo', 'Ascoli Piceno', 'Asti', 'Avellino',
  'Bari', 'Barletta-Andria-Trani', 'Belluno', 'Benevento', 'Bergamo', 'Biella', 'Bologna', 'Bolzano', 'Brescia', 'Brindisi',
  'Cagliari', 'Caltanissetta', 'Campobasso', 'Caserta', 'Catania', 'Catanzaro', 'Chieti', 'Como', 'Cosenza', 'Cremona', 'Crotone', 'Cuneo',
  'Enna',
  'Fermo', 'Ferrara', 'Firenze', 'Foggia', 'Forlì-Cesena', 'Frosinone',
  'Genova', 'Gorizia', 'Grosseto',
  'Imperia', 'Isernia',
  'La Spezia', 'L\'Aquila', 'Latina', 'Lecce', 'Lecco', 'Livorno', 'Lodi', 'Lucca',
  'Macerata', 'Mantova', 'Massa-Carrara', 'Matera', 'Messina', 'Milano', 'Modena', 'Monza e Brianza',
  'Napoli', 'Novara', 'Nuoro',
  'Oristano',
  'Padova', 'Palermo', 'Parma', 'Pavia', 'Perugia', 'Pesaro e Urbino', 'Pescara', 'Piacenza', 'Pisa', 'Pistoia', 'Pordenone', 'Potenza', 'Prato',
  'Ragusa', 'Ravenna', 'Reggio Calabria', 'Reggio Emilia', 'Rieti', 'Rimini', 'Roma', 'Rovigo',
  'Salerno', 'Sassari', 'Savona', 'Siena', 'Siracusa', 'Sondrio', 'Sud Sardegna',
  'Taranto', 'Teramo', 'Terni', 'Torino', 'Trapani', 'Trento', 'Treviso', 'Trieste',
  'Udine',
  'Varese', 'Venezia', 'Verbano-Cusio-Ossola', 'Vercelli', 'Verona', 'Vibo Valentia', 'Vicenza', 'Viterbo'
];

const ContattiPage = () => {
  const [requestType, setRequestType] = useState(null); // 'simple' | 'preventivo'
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    client_type: '',
    name: '',
    email: '',
    phone: '',
    province: '',
    company_name: '',
    message: '',
    // Quote specific
    building_type: '',
    roof_type: '',
    roof_slopes: 0,
    roof_material: '',
    roof_surface: '',
    service_type: '',
    has_existing_system: false,
    notes: ''
  });

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const endpoint = requestType === 'simple' ? '/contact/simple' : '/contact/quote';
      const payload = requestType === 'simple' 
        ? {
            request_type: 'simple',
            client_type: formData.client_type,
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            province: formData.province,
            company_name: formData.company_name || null,
            message: formData.message || null
          }
        : {
            request_type: 'preventivo',
            ...formData
          };

      await axios.post(`${API}${endpoint}`, payload);
      setSubmitted(true);
      toast.success('Richiesta inviata con successo!');
    } catch (error) {
      console.error('Error submitting:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (requestType === 'simple') {
      switch (step) {
        case 1: return formData.client_type;
        case 2: return formData.name && formData.email && formData.phone && formData.province;
        default: return true;
      }
    } else {
      switch (step) {
        case 1: return formData.client_type;
        case 2: return formData.building_type;
        case 3: return formData.roof_type;
        case 4: return formData.service_type;
        case 5: return formData.name && formData.email && formData.phone && formData.province;
        default: return true;
      }
    }
  };

  const totalSteps = requestType === 'simple' ? 3 : 6;

  // Reset form
  const resetForm = () => {
    setRequestType(null);
    setStep(0);
    setSubmitted(false);
    setFormData({
      client_type: '',
      name: '',
      email: '',
      phone: '',
      province: '',
      company_name: '',
      message: '',
      building_type: '',
      roof_type: '',
      roof_slopes: 0,
      roof_material: '',
      roof_surface: '',
      service_type: '',
      has_existing_system: false,
      notes: ''
    });
  };

  // Success Screen
  if (submitted) {
    return (
      <div className="bg-revo-bg pt-20 min-h-screen">
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-8"
          >
            <CheckCircle2 className="w-12 h-12 text-green-500" />
          </motion.div>
          <h1 className="font-barlow text-4xl md:text-5xl font-bold uppercase text-white mb-4">
            Richiesta Inviata!
          </h1>
          <p className="font-manrope text-lg text-gray-400 mb-8">
            {requestType === 'simple' 
              ? 'Ti contatteremo il prima possibile.'
              : 'Riceverai un preventivo personalizzato entro 48 ore.'}
          </p>
          <button
            onClick={resetForm}
            className="inline-flex items-center gap-2 text-revo-orange hover:underline"
            data-testid="new-request-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Nuova Richiesta
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-revo-bg pt-20 min-h-screen">
      {/* Hero */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left - Info */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="font-manrope text-sm text-revo-orange uppercase tracking-widest">
                Contattaci
              </span>
              <h1 className="font-barlow text-5xl md:text-6xl font-bold uppercase tracking-tighter text-white mt-4 mb-6">
                Richiedi Un<br />
                <span className="text-gray-500">Preventivo</span>
              </h1>
              <p className="font-manrope text-lg text-gray-300 leading-relaxed mb-8">
                Compila il nostro configuratore per ricevere un preventivo personalizzato, 
                oppure contattaci direttamente per qualsiasi informazione.
              </p>

              {/* Contact Info */}
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-revo-paper border border-revo-border flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-revo-orange" />
                  </div>
                  <div>
                    <p className="font-manrope text-white">Via Alberto Dominutti, 6</p>
                    <p className="font-manrope text-gray-500 text-sm">37135 Verona (VR)</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-revo-paper border border-revo-border flex items-center justify-center">
                    <Phone className="w-5 h-5 text-revo-orange" />
                  </div>
                  <a href="tel:800864621" className="font-manrope text-white hover:text-revo-orange transition-colors">
                    800 86 46 21 (Numero Verde)
                  </a>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-revo-paper border border-revo-border flex items-center justify-center">
                    <Mail className="w-5 h-5 text-revo-orange" />
                  </div>
                  <a href="mailto:info@revoquota.com" className="font-manrope text-white hover:text-revo-orange transition-colors">
                    info@revoquota.com
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Right - Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-revo-paper border border-revo-border"
            >
              {/* Request Type Selection */}
              {requestType === null && (
                <div className="p-8" data-testid="request-type-selection">
                  <h2 className="font-barlow text-2xl font-bold uppercase tracking-wide text-white mb-6">
                    Come Possiamo Aiutarti?
                  </h2>
                  <div className="space-y-4">
                    <button
                      onClick={() => { setRequestType('simple'); setStep(1); }}
                      className="w-full p-6 bg-revo-bg border border-revo-border hover:border-revo-orange/50 transition-all group text-left"
                      data-testid="request-type-simple"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-revo-subtle flex items-center justify-center group-hover:bg-revo-orange transition-colors">
                          <MessageSquare className="w-6 h-6 text-gray-400 group-hover:text-white" />
                        </div>
                        <div>
                          <h3 className="font-barlow text-lg font-bold uppercase text-white group-hover:text-revo-orange transition-colors">
                            Richiesta Semplice
                          </h3>
                          <p className="font-manrope text-sm text-gray-500 mt-1">
                            Contattaci per informazioni generali o domande
                          </p>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => { setRequestType('preventivo'); setStep(1); }}
                      className="w-full p-6 bg-revo-bg border border-revo-orange/30 hover:border-revo-orange transition-all group text-left relative overflow-hidden"
                      data-testid="request-type-preventivo"
                    >
                      <div className="absolute top-2 right-2 px-2 py-1 bg-revo-orange text-white text-xs font-barlow uppercase">
                        Consigliato
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-revo-orange/20 flex items-center justify-center group-hover:bg-revo-orange transition-colors">
                          <FileText className="w-6 h-6 text-revo-orange group-hover:text-white" />
                        </div>
                        <div>
                          <h3 className="font-barlow text-lg font-bold uppercase text-white group-hover:text-revo-orange transition-colors">
                            Richiedi Preventivo
                          </h3>
                          <p className="font-manrope text-sm text-gray-500 mt-1">
                            Configura il tuo progetto e ricevi un preventivo dettagliato
                          </p>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* Step Form */}
              {requestType && (
                <div className="p-8">
                  {/* Progress */}
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-manrope text-sm text-gray-500">
                        Step {step} di {totalSteps}
                      </span>
                      <span className="font-manrope text-sm text-revo-orange">
                        {Math.round((step / totalSteps) * 100)}%
                      </span>
                    </div>
                    <div className="h-1 bg-revo-bg rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-revo-orange"
                        initial={{ width: 0 }}
                        animate={{ width: `${(step / totalSteps) * 100}%` }}
                      />
                    </div>
                  </div>

                  <AnimatePresence mode="wait">
                    {/* Step 1: Client Type */}
                    {step === 1 && (
                      <motion.div
                        key="step1"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        data-testid="step-client-type"
                      >
                        <h3 className="font-barlow text-xl font-bold uppercase text-white mb-6">
                          Sei un privato o un'azienda?
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                          {['privato', 'azienda'].map((type) => (
                            <button
                              key={type}
                              onClick={() => updateFormData('client_type', type)}
                              className={`p-6 border transition-all ${
                                formData.client_type === type
                                  ? 'bg-revo-orange/10 border-revo-orange'
                                  : 'bg-revo-bg border-revo-border hover:border-revo-orange/50'
                              }`}
                              data-testid={`client-type-${type}`}
                            >
                              <div className="text-4xl mb-3">
                                {type === 'privato' ? <User className="w-10 h-10 text-revo-orange" /> : <Building2 className="w-10 h-10 text-revo-orange" />}
                              </div>
                              <span className="font-barlow text-lg font-bold uppercase text-white">
                                {type === 'privato' ? 'Privato' : 'Azienda'}
                              </span>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}

                    {/* Simple Form - Step 2: Contact Info */}
                    {requestType === 'simple' && step === 2 && (
                      <motion.div
                        key="simple-step2"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-4"
                        data-testid="step-contact-info"
                      >
                        <h3 className="font-barlow text-xl font-bold uppercase text-white mb-6">
                          I Tuoi Dati
                        </h3>
                        
                        {formData.client_type === 'azienda' && (
                          <div>
                            <Label className="text-gray-400">Ragione Sociale</Label>
                            <Input
                              value={formData.company_name}
                              onChange={(e) => updateFormData('company_name', e.target.value)}
                              className="bg-revo-bg border-revo-border text-white mt-1"
                              placeholder="Nome azienda"
                              data-testid="input-company"
                            />
                          </div>
                        )}
                        
                        <div>
                          <Label className="text-gray-400">Nome e Cognome *</Label>
                          <Input
                            value={formData.name}
                            onChange={(e) => updateFormData('name', e.target.value)}
                            className="bg-revo-bg border-revo-border text-white mt-1"
                            placeholder="Mario Rossi"
                            data-testid="input-name"
                          />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-gray-400">Email *</Label>
                            <Input
                              type="email"
                              value={formData.email}
                              onChange={(e) => updateFormData('email', e.target.value)}
                              className="bg-revo-bg border-revo-border text-white mt-1"
                              placeholder="email@esempio.it"
                              data-testid="input-email"
                            />
                          </div>
                          <div>
                            <Label className="text-gray-400">Telefono *</Label>
                            <Input
                              type="tel"
                              value={formData.phone}
                              onChange={(e) => updateFormData('phone', e.target.value)}
                              className="bg-revo-bg border-revo-border text-white mt-1"
                              placeholder="+39 333 1234567"
                              data-testid="input-phone"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label className="text-gray-400">Provincia *</Label>
                          <select
                            value={formData.province}
                            onChange={(e) => updateFormData('province', e.target.value)}
                            className="w-full h-10 bg-revo-bg border border-revo-border text-white px-3 mt-1 focus:border-revo-orange focus:outline-none"
                            data-testid="select-province"
                          >
                            <option value="">Seleziona provincia</option>
                            {provinces.map((p) => (
                              <option key={p} value={p}>{p}</option>
                            ))}
                          </select>
                        </div>
                      </motion.div>
                    )}

                    {/* Simple Form - Step 3: Message */}
                    {requestType === 'simple' && step === 3 && (
                      <motion.div
                        key="simple-step3"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        data-testid="step-message"
                      >
                        <h3 className="font-barlow text-xl font-bold uppercase text-white mb-6">
                          Il Tuo Messaggio
                        </h3>
                        <Textarea
                          value={formData.message}
                          onChange={(e) => updateFormData('message', e.target.value)}
                          className="bg-revo-bg border-revo-border text-white min-h-[150px]"
                          placeholder="Scrivi la tua richiesta..."
                          data-testid="textarea-message"
                        />
                      </motion.div>
                    )}

                    {/* Quote Form - Step 2: Building Type */}
                    {requestType === 'preventivo' && step === 2 && (
                      <motion.div
                        key="quote-step2"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        data-testid="step-building-type"
                      >
                        <h3 className="font-barlow text-xl font-bold uppercase text-white mb-6">
                          Tipologia di Edificio
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                          {buildingTypes[formData.client_type]?.map((building) => (
                            <button
                              key={building.id}
                              onClick={() => updateFormData('building_type', building.id)}
                              className={`p-4 border transition-all text-left ${
                                formData.building_type === building.id
                                  ? 'bg-revo-orange/10 border-revo-orange'
                                  : 'bg-revo-bg border-revo-border hover:border-revo-orange/50'
                              }`}
                              data-testid={`building-type-${building.id}`}
                            >
                              <span className="text-2xl block mb-2">{building.icon}</span>
                              <span className="font-manrope text-sm text-white">
                                {building.label}
                              </span>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}

                    {/* Quote Form - Step 3: Roof Type */}
                    {requestType === 'preventivo' && step === 3 && (
                      <motion.div
                        key="quote-step3"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        data-testid="step-roof-type"
                      >
                        <h3 className="font-barlow text-xl font-bold uppercase text-white mb-6">
                          Tipologia di Tetto
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                          {roofTypes.map((roof) => {
                            const RoofComponent = roof.component;
                            const selected = formData.roof_type === roof.id;
                            return (
                              <button
                                key={roof.id}
                                onClick={() => {
                                  updateFormData('roof_type', roof.id);
                                  if (roof.id === 'piano') {
                                    updateFormData('roof_slopes', 0);
                                  } else if (roof.id === 'pendente_1') {
                                    updateFormData('roof_slopes', 1);
                                  } else if (roof.id === 'pendente_2') {
                                    updateFormData('roof_slopes', 2);
                                  } else {
                                    updateFormData('roof_slopes', 4);
                                  }
                                }}
                                className={`p-6 border transition-all ${
                                  selected
                                    ? 'bg-revo-orange/10 border-revo-orange'
                                    : 'bg-revo-bg border-revo-border hover:border-revo-orange/50'
                                }`}
                                data-testid={`roof-type-${roof.id}`}
                              >
                                <div className="w-full h-20 mb-3">
                                  <RoofComponent selected={selected} />
                                </div>
                                <span className="font-manrope text-sm text-white">
                                  {roof.label}
                                </span>
                              </button>
                            );
                          })}
                        </div>

                        {/* Additional roof info */}
                        <div className="mt-6 space-y-4">
                          <div>
                            <Label className="text-gray-400">Materiale Copertura *</Label>
                            <select
                              value={formData.roof_material}
                              onChange={(e) => updateFormData('roof_material', e.target.value)}
                              className="w-full h-10 bg-revo-bg border border-revo-border text-white px-3 mt-1"
                              data-testid="select-roof-material"
                            >
                              <option value="">Seleziona materiale</option>
                              <optgroup label="Tegole e Coppi">
                                <option value="coppi">Coppi in laterizio</option>
                                <option value="tegole_marsigliesi">Tegole marsigliesi</option>
                                <option value="tegole_portoghesi">Tegole portoghesi</option>
                                <option value="tegole_cemento">Tegole in cemento</option>
                              </optgroup>
                              <optgroup label="Lamiera e Metallo">
                                <option value="lamiera_grecata">Lamiera grecata</option>
                                <option value="lamiera_ondulata">Lamiera ondulata</option>
                                <option value="pannelli_sandwich">Pannelli sandwich/coibentati</option>
                                <option value="alluminio">Alluminio</option>
                                <option value="rame">Rame</option>
                                <option value="zinco_titanio">Zinco-titanio</option>
                              </optgroup>
                              <optgroup label="Guaine e Membrane">
                                <option value="guaina_bituminosa">Guaina bituminosa</option>
                                <option value="guaina_ardesiata">Guaina ardesiata</option>
                                <option value="membrana_pvc">Membrana PVC</option>
                                <option value="membrana_tpo">Membrana TPO</option>
                                <option value="membrana_epdm">Membrana EPDM</option>
                              </optgroup>
                              <optgroup label="Altri Materiali">
                                <option value="eternit">Eternit/Fibrocemento</option>
                                <option value="canadesi">Tegole canadesi (bituminose)</option>
                                <option value="ardesia">Ardesia naturale</option>
                                <option value="cemento_cls">Cemento/CLS</option>
                                <option value="legno">Legno</option>
                                <option value="vetro">Vetro/Lucernari</option>
                                <option value="fotovoltaico">Pannelli fotovoltaici</option>
                                <option value="verde">Tetto verde</option>
                                <option value="misto">Materiali misti</option>
                                <option value="altro">Altro (specificare nelle note)</option>
                              </optgroup>
                            </select>
                          </div>
                          <div>
                            <Label className="text-gray-400">Superficie Indicativa (m²)</Label>
                            <Input
                              type="number"
                              value={formData.roof_surface}
                              onChange={(e) => updateFormData('roof_surface', e.target.value)}
                              className="bg-revo-bg border-revo-border text-white mt-1"
                              placeholder="Es. 200"
                              data-testid="input-roof-surface"
                            />
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Quote Form - Step 4: Service Type */}
                    {requestType === 'preventivo' && step === 4 && (
                      <motion.div
                        key="quote-step4"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        data-testid="step-service-type"
                      >
                        <h3 className="font-barlow text-xl font-bold uppercase text-white mb-6">
                          Servizio Richiesto
                        </h3>
                        <div className="space-y-3">
                          {serviceTypes.map((service) => (
                            <button
                              key={service.id}
                              onClick={() => updateFormData('service_type', service.id)}
                              className={`w-full p-4 border transition-all text-left ${
                                formData.service_type === service.id
                                  ? 'bg-revo-orange/10 border-revo-orange'
                                  : 'bg-revo-bg border-revo-border hover:border-revo-orange/50'
                              }`}
                              data-testid={`service-type-${service.id}`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="font-barlow text-white font-bold uppercase">
                                    {service.label}
                                  </span>
                                  <p className="font-manrope text-sm text-gray-500 mt-1">
                                    {service.description}
                                  </p>
                                </div>
                                {formData.service_type === service.id && (
                                  <CheckCircle2 className="w-6 h-6 text-revo-orange" />
                                )}
                              </div>
                            </button>
                          ))}
                        </div>

                        <div className="mt-6">
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.has_existing_system}
                              onChange={(e) => updateFormData('has_existing_system', e.target.checked)}
                              className="w-5 h-5 bg-revo-bg border-revo-border rounded"
                              data-testid="checkbox-existing-system"
                            />
                            <span className="font-manrope text-gray-400">
                              È già presente un sistema anticaduta
                            </span>
                          </label>
                        </div>
                      </motion.div>
                    )}

                    {/* Quote Form - Step 5: Contact Info */}
                    {requestType === 'preventivo' && step === 5 && (
                      <motion.div
                        key="quote-step5"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="space-y-4"
                        data-testid="step-quote-contact"
                      >
                        <h3 className="font-barlow text-xl font-bold uppercase text-white mb-6">
                          I Tuoi Dati
                        </h3>
                        
                        {formData.client_type === 'azienda' && (
                          <div>
                            <Label className="text-gray-400">Ragione Sociale</Label>
                            <Input
                              value={formData.company_name}
                              onChange={(e) => updateFormData('company_name', e.target.value)}
                              className="bg-revo-bg border-revo-border text-white mt-1"
                              placeholder="Nome azienda"
                              data-testid="input-company-quote"
                            />
                          </div>
                        )}
                        
                        <div>
                          <Label className="text-gray-400">Nome e Cognome *</Label>
                          <Input
                            value={formData.name}
                            onChange={(e) => updateFormData('name', e.target.value)}
                            className="bg-revo-bg border-revo-border text-white mt-1"
                            placeholder="Mario Rossi"
                            data-testid="input-name-quote"
                          />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-gray-400">Email *</Label>
                            <Input
                              type="email"
                              value={formData.email}
                              onChange={(e) => updateFormData('email', e.target.value)}
                              className="bg-revo-bg border-revo-border text-white mt-1"
                              placeholder="email@esempio.it"
                              data-testid="input-email-quote"
                            />
                          </div>
                          <div>
                            <Label className="text-gray-400">Telefono *</Label>
                            <Input
                              type="tel"
                              value={formData.phone}
                              onChange={(e) => updateFormData('phone', e.target.value)}
                              className="bg-revo-bg border-revo-border text-white mt-1"
                              placeholder="+39 333 1234567"
                              data-testid="input-phone-quote"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label className="text-gray-400">Provincia *</Label>
                          <select
                            value={formData.province}
                            onChange={(e) => updateFormData('province', e.target.value)}
                            className="w-full h-10 bg-revo-bg border border-revo-border text-white px-3 mt-1 focus:border-revo-orange focus:outline-none"
                            data-testid="select-province-quote"
                          >
                            <option value="">Seleziona provincia</option>
                            {provinces.map((p) => (
                              <option key={p} value={p}>{p}</option>
                            ))}
                          </select>
                        </div>
                      </motion.div>
                    )}

                    {/* Quote Form - Step 6: Notes */}
                    {requestType === 'preventivo' && step === 6 && (
                      <motion.div
                        key="quote-step6"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        data-testid="step-notes"
                      >
                        <h3 className="font-barlow text-xl font-bold uppercase text-white mb-6">
                          Note Aggiuntive
                        </h3>
                        <Textarea
                          value={formData.notes}
                          onChange={(e) => updateFormData('notes', e.target.value)}
                          className="bg-revo-bg border-revo-border text-white min-h-[120px]"
                          placeholder="Aggiungi eventuali dettagli o richieste specifiche..."
                          data-testid="textarea-notes"
                        />

                        {/* Summary */}
                        <div className="mt-6 p-4 bg-revo-bg border border-revo-border">
                          <h4 className="font-barlow text-sm font-bold uppercase text-gray-400 mb-3">
                            Riepilogo Richiesta
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-500">Tipo Cliente:</span>
                              <span className="text-white capitalize">{formData.client_type}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Edificio:</span>
                              <span className="text-white">{buildingTypes[formData.client_type]?.find(b => b.id === formData.building_type)?.label}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Tetto:</span>
                              <span className="text-white">{roofTypes.find(r => r.id === formData.roof_type)?.label}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Servizio:</span>
                              <span className="text-revo-orange">{serviceTypes.find(s => s.id === formData.service_type)?.label}</span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Navigation */}
                  <div className="flex items-center justify-between mt-8 pt-6 border-t border-revo-border">
                    {step > 1 ? (
                      <button
                        onClick={() => setStep(step - 1)}
                        className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                        data-testid="btn-prev"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        <span className="font-manrope text-sm">Indietro</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => { setRequestType(null); setStep(0); }}
                        className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                        data-testid="btn-back-type"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        <span className="font-manrope text-sm">Cambia Tipo</span>
                      </button>
                    )}

                    {step < totalSteps ? (
                      <button
                        onClick={() => setStep(step + 1)}
                        disabled={!canProceed()}
                        className={`flex items-center gap-2 px-6 py-3 font-barlow font-bold uppercase tracking-widest text-sm transition-all ${
                          canProceed()
                            ? 'bg-revo-orange text-white hover:bg-revo-orange-dark'
                            : 'bg-revo-border text-gray-600 cursor-not-allowed'
                        }`}
                        data-testid="btn-next"
                      >
                        <span>Avanti</span>
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={handleSubmit}
                        disabled={loading || !canProceed()}
                        className={`flex items-center gap-2 px-6 py-3 font-barlow font-bold uppercase tracking-widest text-sm transition-all ${
                          canProceed() && !loading
                            ? 'bg-revo-orange text-white hover:bg-revo-orange-dark hover:shadow-[0_0_20px_rgba(255,102,0,0.3)]'
                            : 'bg-revo-border text-gray-600 cursor-not-allowed'
                        }`}
                        data-testid="btn-submit"
                      >
                        {loading ? (
                          <>
                            <span className="animate-spin">⏳</span>
                            <span>Invio...</span>
                          </>
                        ) : (
                          <>
                            <span>Invia Richiesta</span>
                            <Send className="w-4 h-4" />
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContattiPage;
